import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { z } from 'zod';
import { API_CONFIG, HTTP_STATUS } from '@/lib/constants';
import { logger } from '@/lib/logger';

// Error types
export class ApiError extends Error {
  readonly _tag = 'ApiError';
  constructor(
    public readonly message: string,
    public readonly statusCode?: number,
    public readonly details?: unknown
  ) {
    super(message);
  }
}

export class NetworkError extends Error {
  readonly _tag = 'NetworkError';
  constructor(public readonly message: string) {
    super(message);
  }
}

export class ValidationError extends Error {
  readonly _tag = 'ValidationError';
  constructor(public readonly errors: z.ZodError) {
    super(errors.message);
  }
}

export class AuthenticationError extends Error {
  readonly _tag = 'AuthenticationError';
  constructor(public readonly message: string) {
    super(message);
  }
}

// Config service
export class ApiConfig extends Context.Tag('ApiConfig')<
  ApiConfig,
  {
    readonly baseUrl: string;
    readonly timeout: number;
  }
>() {}

// Auth store service interface
export class AuthStore extends Context.Tag('AuthStore')<
  AuthStore,
  {
    readonly getToken: () => Promise<string | null>;
    readonly refreshToken: () => Promise<string>;
    readonly clearAuth: () => Promise<void>;
    readonly setTokens: (accessToken: string, refreshToken?: string) => Promise<void>;
  }
>() {}

// HTTP client service
export class HttpClient extends Context.Tag('HttpClient')<
  HttpClient,
  {
    readonly request: <T>(
      method: string,
      path: string,
      body?: unknown,
      headers?: Record<string, string>
    ) => Effect.Effect<T, ApiError | NetworkError | AuthenticationError>;
  }
>() {}

// Response type for ETag-aware requests
export interface ETagResponse<T> {
  readonly notModified: boolean;
  readonly data: T | null;
}

// API Client service
export class ApiClient extends Context.Tag('ApiClient')<
  ApiClient,
  {
    readonly get: <T>(
      path: string,
      schema?: z.ZodSchema<T>
    ) => Effect.Effect<T, ApiError | NetworkError | ValidationError | AuthenticationError>;

    readonly post: <T>(
      path: string,
      body?: unknown,
      schema?: z.ZodSchema<T>
    ) => Effect.Effect<T, ApiError | NetworkError | ValidationError | AuthenticationError>;

    readonly put: <T>(
      path: string,
      body: unknown,
      schema?: z.ZodSchema<T>
    ) => Effect.Effect<T, ApiError | NetworkError | ValidationError | AuthenticationError>;

    readonly delete: (
      path: string
    ) => Effect.Effect<void, ApiError | NetworkError | AuthenticationError>;

    readonly clearGroupETags: (groupId: string) => Effect.Effect<void, never>;
  }
>() {}

// ETag storage - IndexedDB for persistence across reloads
const ETAG_DB_NAME = 'ETagCache';
const ETAG_STORE_NAME = 'etags';
const ETAG_DB_VERSION = 2; // Keep at 2 to avoid downgrade error

let etagDB: IDBDatabase | null = null;

// Initialize IndexedDB for ETag storage
async function initETagDB(): Promise<IDBDatabase> {
  if (etagDB) return etagDB;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ETAG_DB_NAME, ETAG_DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      etagDB = request.result;
      resolve(etagDB);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(ETAG_STORE_NAME)) {
        db.createObjectStore(ETAG_STORE_NAME);
      }
      // Create cache store for version 2 compatibility (not currently used)
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache');
      }
    };
  });
}

// Get ETag from IndexedDB
async function getETag(url: string): Promise<string | null> {
  try {
    const db = await initETagDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([ETAG_STORE_NAME], 'readonly');
      const store = transaction.objectStore(ETAG_STORE_NAME);
      const request = store.get(url);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logger.error('[ETag] Failed to get ETag from IndexedDB', { error });
    return null;
  }
}

// Store ETag in IndexedDB
async function setETag(url: string, etag: string): Promise<void> {
  try {
    const db = await initETagDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([ETAG_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(ETAG_STORE_NAME);
      const request = store.put(etag, url);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logger.error('[ETag] Failed to store ETag in IndexedDB', { error });
  }
}

// Delete ETags matching a pattern (for clearing after mutations)
async function deleteETags(urlPattern: string): Promise<void> {
  try {
    const db = await initETagDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([ETAG_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(ETAG_STORE_NAME);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        const keys = request.result as string[];
        const keysToDelete = keys.filter(key => key.includes(urlPattern));

        keysToDelete.forEach(key => {
          store.delete(key);
          logger.debug('[ETag] Cleared ETag', { url: key });
        });

        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    logger.error('[ETag] Failed to delete ETags from IndexedDB', { error });
  }
}

// HTTP Client implementation
export const HttpClientLive = Layer.effect(
  HttpClient,
  Effect.gen(function* (_) {
    const config = yield* _(ApiConfig);
    const authStore = yield* _(AuthStore);

    const makeRequest = <T>(
      method: string,
      path: string,
      body?: unknown,
      extraHeaders?: Record<string, string>
    ): Effect.Effect<T, ApiError | NetworkError | AuthenticationError> =>
      pipe(
        Effect.tryPromise({
          try: async () => {
            const token = await authStore.getToken();
            const controller = new AbortController();
            const timeoutId = setTimeout(
              () => controller.abort(),
              config.timeout
            );

            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              ...extraHeaders,
            };

            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }

            // Add If-None-Match header for GET requests with cached ETags
            // Only for endpoints that support ETags
            const url = `${config.baseUrl}${path}`;
            const supportsETag = method === 'GET' && (
              path.includes('/expenses') ||
              path.includes('/settlements')
            );

            if (supportsETag) {
              const cachedETag = await getETag(url);
              logger.debug('[ETag] Checking cache', { url, cachedETag: cachedETag || 'none' });
              if (cachedETag) {
                headers['If-None-Match'] = cachedETag;
                logger.debug('[ETag] Adding If-None-Match header', { url, etag: cachedETag });
              }
            }

            try {
              logger.debug('HTTP request', { url, method, body });
              const response = await fetch(url, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
                signal: controller.signal,
              });

              clearTimeout(timeoutId);

              // Handle 304 Not Modified - return special marker
              if (response.status === 304) {
                logger.debug('[ETag] 304 Not Modified', { url });
                return {
                  __notModified: true,
                  __url: url
                } as any;
              }

              // 401 means the token is invalid or expired. Tokens are
              // long-lived (90 days) and the backend has no refresh endpoint,
              // so there is nothing to retry — clear auth and surface a
              // session-expired error. (The previous code POSTed to a
              // nonexistent /auth/refresh before landing in the same place.)
              if (response.status === HTTP_STATUS.UNAUTHORIZED) {
                await authStore.clearAuth();
                throw new AuthenticationError('Session expired');
              }

              if (!response.ok) {
                const errorText = await response.text();
                throw new ApiError(errorText, response.status);
              }

              // Store ETag from successful GET responses (only for supported endpoints)
              if (supportsETag) {
                const etag = response.headers.get('ETag');
                if (etag) {
                  await setETag(url, etag);
                  logger.debug('[ETag] Stored ETag', { url, etag });
                }
              }

              // Handle 204 No Content
              if (response.status === HTTP_STATUS.NO_CONTENT) {
                return null;
              }

              const data = await response.json();
              logger.debug('HTTP response', { data });
              return data;
            } finally {
              clearTimeout(timeoutId);
            }
          },
          catch: (error) => {
            if (error instanceof ApiError || error instanceof AuthenticationError) {
              return error;
            }
            if (error instanceof Error) {
              if (error.name === 'AbortError') {
                return new NetworkError('Request timeout');
              }
              return new NetworkError(error.message);
            }
            return new NetworkError('Unknown network error');
          },
        })
      );

    return {
      request: makeRequest,
    };
  })
);

// API Client implementation
export const ApiClientLive = Layer.effect(
  ApiClient,
  Effect.gen(function* () {
    const httpClient = yield* HttpClient;
    const config = yield* ApiConfig;

    const validateResponse = <T>(
      data: unknown,
      schema?: z.ZodSchema<T>
    ): Effect.Effect<T, ValidationError> => {
      if (!schema) {
        return Effect.succeed(data as T);
      }

      const result = schema.safeParse(data);
      if (!result.success) {
        logger.error('Validation failed', result.error, { data });
        return Effect.fail(new ValidationError(result.error));
      }

      return Effect.succeed(result.data);
    };

    return {
      get: <T>(path: string, schema?: z.ZodSchema<T>) =>
        pipe(
          httpClient.request<T>('GET', path),
          Effect.flatMap((data: any) => {
            // Check if this is a 304 Not Modified marker
            if (data?.__notModified === true) {
              // Return special response indicating data unchanged
              return Effect.succeed({
                notModified: true,
                data: null
              } as any);
            }
            // Normal response - just return the validated data (no wrapper)
            return validateResponse(data, schema);
          })
        ),

      post: <T>(path: string, body?: unknown, schema?: z.ZodSchema<T>) =>
        pipe(
          httpClient.request<T>('POST', path, body),
          Effect.flatMap((data) => validateResponse(data, schema))
        ),

      put: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
        pipe(
          httpClient.request<T>('PUT', path, body),
          Effect.flatMap((data) => validateResponse(data, schema))
        ),

      delete: (path: string) =>
        pipe(
          httpClient.request<void>('DELETE', path),
          Effect.map(() => undefined)
        ),

      clearGroupETags: (groupId: string) =>
        Effect.promise(() => {
          const baseUrl = config.baseUrl;
          const urlPattern = `${baseUrl}/groups/${groupId}/`;
          return deleteETags(urlPattern);
        })
    };
  })
);

// Configuration layers
export const ApiConfigLive = Layer.succeed(ApiConfig, {
  baseUrl: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
});

// Auth Store implementation layer
export const AuthStoreLive = Layer.succeed(AuthStore, {
  getToken: async () => {
    const { useAuthStore } = await import('@/stores/auth.store');
    const state = useAuthStore.getState();
    return state.accessToken;
  },
  refreshToken: async () => {
    const { useAuthStore } = await import('@/stores/auth.store');
    const state = useAuthStore.getState();
    return state.refreshTokens();
  },
  clearAuth: async () => {
    const { useAuthStore } = await import('@/stores/auth.store');
    const state = useAuthStore.getState();
    state.logout();
  },
  setTokens: async (accessToken: string, refreshToken?: string) => {
    const { useAuthStore } = await import('@/stores/auth.store');
    const state = useAuthStore.getState();
    state.setTokens(accessToken, refreshToken);
  },
});

// Build the complete application layer with all services
// We need all services to be available in the final layer
const configLayer = Layer.mergeAll(ApiConfigLive, AuthStoreLive);

// Create HttpClient with its dependencies
const httpLayer = Layer.provideMerge(HttpClientLive, configLayer);

// Create ApiClient with HttpClient available, and merge everything together
const apiLayer = Layer.provideMerge(ApiClientLive, httpLayer);

// The final layer needs to expose ALL services
export const AppLayer = Layer.mergeAll(
  configLayer,
  httpLayer,
  apiLayer
);

// Utility function to run effects with the app layer
export const runWithAppLayer = <A, E>(effect: Effect.Effect<A, E>) =>
  pipe(effect, Effect.provide(AppLayer));
